import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";

const fmtINR = (n) =>
  Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

const CourseRegistrationPage = () => {
  const [selectedCourse, setSelectedCourse] = useState("");
  const [mode, setMode] = useState("online"); // online | offline | ml-sprint

  // ✅ Only payment methods that match Offline page backend contract
  const [payMode, setPayMode] = useState("upi"); // "upi" | "card_nb"

  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phoneNumber: "",
  });

  const [isMobile, setIsMobile] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState("");

  // ✅ left illustration fallback
  const [leftImgSrc, setLeftImgSrc] = useState(
    // primary (may get blocked by hotlink protection)
    "https://scitechdaily.com/images/Left-Right-Brain-Signals.gif"
  );

  // AWS API Gateway Base URL
  const API_BASE_URL =
    "https://qlw95zx5ta.execute-api.eu-north-1.amazonaws.com/t/payment";

  const courses = useMemo(
    () => [
      {
        id: "ai-agentic",
        name: "Advanced Artificial Intelligence & Agentic Systems Program",
        duration: "(12-14) weeks / (3-3.5) months",
        description:
          "Machine Learning, Deep learning - Neural Networks, NLP, transformer models, LLM models, agentic AI",
        price: "₹5,000",
      },
      {
        id: "data-engineering",
        name: "Advanced Data Engineering & Analytics Program",
        duration: "(15-16) weeks / (3.5-4) months",
        description:
          "Python, data engineering, cloud, SQL, advanced data analysis, ML-1, Tableau",
        price: "₹3,000",
      },
      {
        id: "gen-ai-professional",
        name: "Gen-AI Professional Course - Complete Package",
        duration: "(24-25) weeks / (6-6.5) months",
        description:
          "Comprehensive program combining Advanced Data Engineering, Data analysis, Data Science - All together",
        price: "₹10,000",
      },
      {
        id: "python-bootcamp",
        name: "Python Programming & Data Engineering Bootcamp",
        duration: "(6-7) weeks / (1.5-2) months",
        description:
          "Python programming fundamentals + Data engineering principles",
        price: "No Stipend",
      },
      {
        id: "sql-mastery",
        name: "SQL Mastery & Advanced Data Analysis Bootcamp",
        duration: "(4-5) weeks / (1-1.5) months",
        description:
          "SQL fundamentals to advanced + comprehensive data analysis techniques",
        price: "No Stipend",
      },
      {
        id: "ml-deep-learning",
        name: "Advanced Machine Learning & Deep Learning Sprint",
        duration: "(9-10) weeks / (2.5-3) months",
        description: "ML-1 fundamentals + ML-2 (deep learning) advanced concepts",
        price: "No Stipend",
      },
      {
        id: "ml-sprint-program",
        name: "Project-Driven Advanced ML, Deep Learning & NLP Learning Sprint",
        duration: "1 Month | 4 Days a Week",
        description:
          "Intensive hands-on program covering Machine Learning, Deep Learning & NLP with real-world projects. Limited to 12 students per batch.",
        price: "₹2,000",
        totalFee: "₹10,000",
      },
    ],
    []
  );

  const offlineInfo = useMemo(
    () => ({
      badge: "OFFLINE BATCH INFO",
      note: "Limited seats • First-come-first-serve",
      locations: [
        { city: "Hyderabad", place: "Craftingbrain Manikonda" },
        {
          city: "Location",
          place: "https://maps.app.goo.gl/f4HAPyDK4gtuCh699?g_st=iw",
        },
      ],
      start: "Next batch starts: Coming Soon (Call to confirm)",
      contact: "+91 9115076096",
    }),
    []
  );

  const mlSprintInfo = useMemo(
    () => ({
      badge: "ML SPRINT PROGRAM",
      note: "Limited to 12 Students • Intensive Training",
      features: [
        "Duration: 1 Month (4 Days a Week)",
        "Total Internship Fee: ₹10,000",
        "Registration Amount: ₹2,000",
        "Batch Size: 12 Students Only",
        "Project-Driven Learning Approach",
      ],
      description:
        "Hands-on intensive program focused on real-world ML, DL & NLP projects with personalized mentorship.",
      contact: "+91 9115076096",
    }),
    []
  );

  // ✅ base fee depends on mode (NO GST toggle here)
  const baseAmount = useMemo(() => {
    if (mode === "offline") return 10000;
    if (mode === "ml-sprint") return 2000;
    return 10000;
  }, [mode]);

  // ✅ filtered courses depend on mode
  const filteredCourses = useMemo(() => {
    if (mode === "offline") return courses.filter((c) => c.id === "gen-ai-professional");
    if (mode === "ml-sprint") return courses.filter((c) => c.id === "ml-sprint-program");
    return courses;
  }, [mode, courses]);

  // ✅ auto-select course based on mode
  useEffect(() => {
    if (mode === "offline") {
      const genAi = courses.find((c) => c.id === "gen-ai-professional");
      if (genAi) setSelectedCourse("gen-ai-professional");
    } else if (mode === "ml-sprint") {
      const mlSprint = courses.find((c) => c.id === "ml-sprint-program");
      if (mlSprint) setSelectedCourse("ml-sprint-program");
    }
  }, [mode, courses]);

  useEffect(() => {
    if (!selectedCourse) return;
    const stillExists = filteredCourses.some((c) => c.id === selectedCourse);
    if (!stillExists) setSelectedCourse("");
  }, [filteredCourses, selectedCourse]);

  // ✅ reset payMode when switching modes (optional but cleaner)
  useEffect(() => {
    setPayMode("upi");
  }, [mode]);

  // mobile
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth <= 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // ✅ UI-only breakdown for card/netbanking (backend is source of truth)
  const chargesBreakdown = useMemo(() => {
    if (payMode !== "card_nb") {
      return {
        convenienceFee: 0,
        gstOnFee: 0,
        totalCharges: 0,
        estimatedFinal: baseAmount,
      };
    }
    const convenienceFee = Math.round(baseAmount * 0.02);
    const gstOnFee = Math.round(convenienceFee * 0.18);
    const totalCharges = convenienceFee + gstOnFee;
    const estimatedFinal = baseAmount + totalCharges;
    return { convenienceFee, gstOnFee, totalCharges, estimatedFinal };
  }, [payMode, baseAmount]);

  const totalPayable = chargesBreakdown.estimatedFinal;

  const selectedCourseData = filteredCourses.find((c) => c.id === selectedCourse);

  const getModeDescription = () => {
    if (mode === "offline") {
      return "Offline classroom registration fee. Includes seat confirmation + onboarding support.";
    }
    if (mode === "ml-sprint") {
      return "ML Sprint program registration. Secures your spot in this intensive 1-month learning program.";
    }
    return "Online program registration fee. Secure your spot and get onboarding support.";
  };

  // =======================
  // Razorpay Integration
  // =======================
  const createPaymentOrder = async () => {
    try {
      setLoading(true);
      setPaymentStatus("");

      const response = await axios.post(
        `${API_BASE_URL}/create_order/create-order`,
        {
          email: formData.email,
          amount: baseAmount, // ✅ BASE only
          fullName: formData.fullName,
          phoneNumber: formData.phoneNumber,

          // ✅ match offline page backend
          pay_mode: payMode, // "upi" | "card_nb"
          ui_estimated_total: totalPayable,

          // helpful for backend logs
          mode, // online | offline | ml-sprint

          courseName: selectedCourseData?.name || "",
          courseDuration: selectedCourseData?.duration || "",
          courseDescription: selectedCourseData?.description || "",
          courseId: selectedCourse,
        }
      );

      if (response.data?.status === "success") return response.data;
      throw new Error(response.data?.message || "Failed to create order");
    } catch (error) {
      console.error("Error creating order:", error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const verifyPayment = async (paymentData) => {
    const response = await axios.post(
      `${API_BASE_URL}/verify_payment/verify-payment`,
      {
        razorpay_order_id: paymentData.razorpay_order_id,
        razorpay_payment_id: paymentData.razorpay_payment_id,
        razorpay_signature: paymentData.razorpay_signature,
      }
    );
    return response.data;
  };

  const handlePayment = async () => {
    try {
      const orderData = await createPaymentOrder();
      const am = orderData.allowed_methods || {};

      const options = {
        key: orderData.key,
        amount: orderData.amount,
        currency: orderData.currency,
        name: orderData.name,
        description: orderData.description,
        image: orderData.image,
        order_id: orderData.order_id,
        prefill: orderData.prefill,
        theme: orderData.theme,

        method: {
          upi: !!am.upi,
          card: !!am.card,
          netbanking: !!am.netbanking,
          wallet: false,
        },

        handler: async function (response) {
          try {
            setLoading(true);
            setPaymentStatus("Verifying payment...");
            const verificationResult = await verifyPayment(response);

            if (verificationResult?.status === "success") {
              setPaymentStatus("Payment successful!");
              setShowSuccessModal(true);
            } else {
              setPaymentStatus("Payment verification failed");
              alert("Payment verification failed. Please contact support.");
            }
          } catch (error) {
            setPaymentStatus("Payment verification failed");
            console.error("Verification error:", error);
            alert("Payment verification failed. Please contact support.");
          } finally {
            setLoading(false);
          }
        },

        modal: {
          ondismiss: function () {
            setPaymentStatus("Payment cancelled by user");
            setLoading(false);
          },
        },
      };

      const rzp = new window.Razorpay(options);

      rzp.on("payment.failed", function (response) {
        setPaymentStatus("Payment failed");
        setLoading(false);
        alert(`Payment failed: ${response?.error?.description || "Unknown error"}`);
      });

      rzp.open();
    } catch (error) {
      setPaymentStatus("Error initiating payment");
      setLoading(false);
      console.error("Payment error:", error);
      alert("Error initiating payment. Please try again.");
    }
  };

  // handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCourseSelect = (e) => setSelectedCourse(e.target.value);

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setFormData({ fullName: "", email: "", phoneNumber: "" });
    setSelectedCourse("");
    setAcceptedTerms(false);
    setPaymentStatus("");
    setMode("online");
    setPayMode("upi");
  };

  const handleNext = () => {
    if (!selectedCourse || !formData.fullName || !formData.email || !formData.phoneNumber) {
      alert("⚠ Please fill in all required fields to continue");
      return;
    }
    if (!acceptedTerms) {
      alert("⚠ Please accept the Terms and Conditions to proceed");
      return;
    }
    handlePayment();
  };

  // =======================
  // Floating particles
  // =======================
  const generateParticles = () => {
    const particles = [];
    const particleCount = isMobile ? 10 : 20;
    for (let i = 0; i < particleCount; i++) {
      particles.push(
        <div
          key={i}
          style={{
            position: "absolute",
            width: isMobile ? "3px" : "4px",
            height: isMobile ? "3px" : "4px",
            background: "linear-gradient(45deg, #667eea, #764ba2)",
            borderRadius: "50%",
            left: `${Math.random() * 100}vw`,
            animation: `float ${8 + Math.random() * 4}s infinite linear`,
            animationDelay: `${Math.random() * 8}s`,
            opacity: 0.6,
          }}
        />
      );
    }
    return particles;
  };

  // =======================
  // Inline styles
  // =======================
  const containerStyle = {
    minHeight: "100vh",
    background:
      "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)",
    fontFamily: "Inter, -apple-system, BlinkMacSystemFont, sans-serif",
    position: "relative",
    overflow: "hidden",
  };

  const mainContentStyle = {
    display: "flex",
    flexDirection: isMobile ? "column" : "row",
    minHeight: "100vh",
    maxWidth: isMobile ? "100%" : "1400px",
    margin: "0 auto",
    position: "relative",
    zIndex: 2,
  };

  const leftPanelStyle = {
    flex: isMobile ? "none" : "0 0 400px",
    background: "rgba(255, 255, 255, 0.02)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: isMobile ? "0" : "24px 0 0 24px",
    padding: isMobile ? "1.5rem 1rem" : "2rem",
    display: "flex",
    flexDirection: "column",
    gap: isMobile ? "1.5rem" : "2rem",
    ...(isMobile && {
      borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius: "0",
    }),
  };

  const rightPanelStyle = {
    flex: 1,
    background: "rgba(255, 255, 255, 0.02)",
    backdropFilter: "blur(20px)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderLeft: isMobile ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
    borderTop: isMobile ? "none" : "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: isMobile ? "0" : "0 24px 24px 0",
    padding: isMobile ? "1.5rem 1rem" : "2rem",
  };

  const inputStyle = {
    width: "100%",
    padding: isMobile ? "0.875rem" : "0.75rem",
    background: "rgba(255, 255, 255, 0.05)",
    border: "1px solid rgba(255, 255, 255, 0.2)",
    borderRadius: "8px",
    color: "white",
    fontSize: isMobile ? "16px" : "0.9rem",
    outline: "none",
    boxSizing: "border-box",
  };

  const buttonStyle = {
    width: "100%",
    padding: isMobile ? "1rem" : "0.875rem",
    background: loading
      ? "linear-gradient(135deg, #6b7280, #9ca3af)"
      : "linear-gradient(135deg, #667eea, #764ba2)",
    border: "none",
    borderRadius: "12px",
    color: "white",
    fontSize: isMobile ? "1.1rem" : "1rem",
    fontWeight: "600",
    cursor: loading ? "not-allowed" : "pointer",
    transition: "all 0.3s ease",
    marginTop: "1rem",
    touchAction: "manipulation",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "0.5rem",
  };

  const modeWrap = {
    display: "flex",
    gap: "8px",
    padding: "10px",
    borderRadius: "14px",
    border: "1px solid rgba(255,255,255,0.12)",
    background: "rgba(255,255,255,0.03)",
    flexWrap: isMobile ? "wrap" : "nowrap",
  };

  const modeBtn = (active) => ({
    flex: isMobile ? "1 1 calc(50% - 4px)" : "1",
    padding: isMobile ? "0.85rem 0.5rem" : "0.85rem",
    borderRadius: "12px",
    border: active ? "none" : "1px solid rgba(255,255,255,0.18)",
    background: active
      ? "linear-gradient(135deg, #667eea, #764ba2)"
      : "rgba(255,255,255,0.04)",
    color: active ? "white" : "#cbd5e1",
    fontWeight: 800,
    fontSize: isMobile ? "0.8rem" : "0.85rem",
    cursor: "pointer",
    transition: "transform .2s ease, filter .2s ease",
    textAlign: "center",
  });

  const infoBox = {
    marginTop: "10px",
    borderRadius: "14px",
    border: "1px solid rgba(102,126,234,0.25)",
    background: "rgba(102,126,234,0.10)",
    padding: isMobile ? "0.9rem" : "1rem",
  };

  const smallText = {
    color: "#cbd5e1",
    fontSize: isMobile ? "0.8rem" : "0.85rem",
    lineHeight: 1.55,
  };

  const labelText = {
    color: "#e2e8f0",
    fontSize: isMobile ? "0.85rem" : "0.9rem",
    fontWeight: 600,
    marginBottom: "0.5rem",
    display: "block",
  };

  // ✅ payment method UI styles
  const pmGrid = {
    display: "grid",
    gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
    gap: "10px",
  };

  const pmBtn = (active) => ({
    padding: "0.9rem",
    borderRadius: "12px",
    border: active ? "none" : "1px solid rgba(255,255,255,0.18)",
    background: active
      ? "linear-gradient(135deg, #667eea, #764ba2)"
      : "rgba(255,255,255,0.04)",
    color: active ? "white" : "#cbd5e1",
    fontWeight: 800,
    cursor: "pointer",
    textAlign: "left",
  });

  const hintStyle = {
    marginTop: "8px",
    color: "#94a3b8",
    fontSize: isMobile ? "0.78rem" : "0.82rem",
    lineHeight: 1.4,
  };

  const breakdownBox = {
    background: "rgba(255,255,255,0.03)",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: "12px",
    padding: isMobile ? "0.9rem" : "1rem",
  };

  const bdRow = {
    display: "flex",
    justifyContent: "space-between",
    gap: "10px",
    color: "#cbd5e1",
    fontSize: isMobile ? "0.82rem" : "0.88rem",
    marginBottom: "8px",
  };

  const bdTotal = {
    ...bdRow,
    marginTop: "10px",
    paddingTop: "10px",
    borderTop: "1px solid rgba(255,255,255,0.10)",
    color: "white",
    fontWeight: 900,
    fontSize: isMobile ? "0.95rem" : "1.0rem",
  };

  return (
    <div style={containerStyle}>
      {/* Floating Particles */}
      <div
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          pointerEvents: "none",
          zIndex: 1,
        }}
      >
        {generateParticles()}
      </div>

      {/* CSS Animations */}
      <style>
        {`
          @keyframes float {
            0% { transform: translateY(100vh) rotate(0deg); }
            100% { transform: translateY(-100vh) rotate(360deg); }
          }

          .spinner {
            width: 18px;
            height: 18px;
            border: 3px solid transparent;
            border-top: 3px solid white;
            border-radius: 50%;
            animation: spin 1s linear infinite;
          }

          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }
        `}
      </style>

      <div style={mainContentStyle} className={isMobile ? "mobile-container" : ""}>
        {/* Left Panel */}
        <div style={leftPanelStyle}>
          {/* Brand */}
          <div style={{ textAlign: "center" }}>
            <div style={{ fontSize: isMobile ? "2rem" : "3rem", marginBottom: "0.5rem" }}>🧠</div>
            <div
              style={{
                fontSize: isMobile ? "1.25rem" : "1.5rem",
                fontWeight: 600,
                background: "linear-gradient(135deg, #667eea, #764ba2)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Crafting Brain
            </div>
          </div>

          {/* Summary */}
          <div
            style={{
              background: "rgba(255, 255, 255, 0.05)",
              borderRadius: "16px",
              padding: isMobile ? "1rem" : "1.5rem",
              border: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <h3
              style={{
                color: "white",
                fontSize: isMobile ? "1.1rem" : "1.25rem",
                margin: "0 0 1rem 0",
                fontWeight: 700,
              }}
            >
              Amount Summary
            </h3>

            <div style={{ color: "#a0aec0", fontSize: isMobile ? "0.85rem" : "0.9rem", lineHeight: 1.5, marginBottom: "10px" }}>
              {getModeDescription()}
            </div>

            <div style={breakdownBox}>
              <div style={bdRow}>
                <span>Program Fee</span>
                <b style={{ color: "white" }}>₹{fmtINR(baseAmount)}</b>
              </div>
              <div style={bdRow}>
                <span>Convenience Fee (2%)</span>
                <span>₹{fmtINR(chargesBreakdown.convenienceFee)}</span>
              </div>
              <div style={bdRow}>
                <span>GST on Fee (18%)</span>
                <span>₹{fmtINR(chargesBreakdown.gstOnFee)}</span>
              </div>
              <div style={bdRow}>
                <span>Payment Charges</span>
                <span>₹{fmtINR(chargesBreakdown.totalCharges)}</span>
              </div>
              <div style={bdTotal}>
                <span>Total Payable</span>
                <span>₹{fmtINR(totalPayable)}</span>
              </div>

              {mode === "ml-sprint" && (
                <div
                  style={{
                    marginTop: "0.75rem",
                    padding: "0.6rem",
                    background: "rgba(104, 211, 145, 0.08)",
                    borderRadius: "10px",
                    border: "1px solid rgba(104, 211, 145, 0.18)",
                  }}
                >
                  <div style={{ color: "#68d391", fontSize: isMobile ? "0.75rem" : "0.8rem" }}>
                    Total Program Fee: ₹10,000
                  </div>
                  <div style={{ color: "#a0aec0", fontSize: isMobile ? "0.7rem" : "0.75rem", marginTop: "0.25rem" }}>
                    (Balance ₹8,000 payable after registration)
                  </div>
                </div>
              )}

              <div style={hintStyle}>
                
              </div>
            </div>
          </div>

          {/* Payment Status */}
          {paymentStatus && (
            <div
              style={{
                background: paymentStatus.includes("successful")
                  ? "rgba(16, 185, 129, 0.12)"
                  : paymentStatus.includes("failed") || paymentStatus.includes("Error")
                  ? "rgba(239, 68, 68, 0.12)"
                  : "rgba(59, 130, 246, 0.12)",
                border: `1px solid ${
                  paymentStatus.includes("successful")
                    ? "rgba(16, 185, 129, 0.28)"
                    : paymentStatus.includes("failed") || paymentStatus.includes("Error")
                    ? "rgba(239, 68, 68, 0.28)"
                    : "rgba(59, 130, 246, 0.28)"
                }`,
                borderRadius: "12px",
                padding: isMobile ? "0.875rem" : "1rem",
                color: "#e2e8f0",
                fontWeight: 600,
              }}
            >
              {paymentStatus}
            </div>
          )}

          {/* Help */}
          <div
            style={{
              background: "rgba(102, 126, 234, 0.1)",
              borderRadius: "12px",
              padding: isMobile ? "0.875rem" : "1rem",
              border: "1px solid rgba(102, 126, 234, 0.2)",
              color: "#e2e8f0",
            }}
          >
            Need Help? Call: +91 93050 09726
          </div>

          {/* ✅ Illustration (desktop only) - FIXED with fallback */}
          {!isMobile && (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", alignItems: "center" }}>
              <div
                style={{
                  width: "200px",
                  height: "150px",
                  background: "rgba(255, 255, 255, 0.05)",
                  borderRadius: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  marginBottom: "1rem",
                  border: "1px solid rgba(255, 255, 255, 0.1)",
                  overflow: "hidden",
                }}
              >
                <img
                  src={leftImgSrc}
                  alt="Educational Animation"
                  loading="lazy"
                  referrerPolicy="no-referrer"
                  onError={() => {
                    // fallback that almost never blocks
                    setLeftImgSrc("https://media.giphy.com/media/l0HlBO7eyXzSZkJri/giphy.gif");
                  }}
                  style={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </div>
              <div style={{ color: "#68d391", fontSize: "0.9rem", fontWeight: 600 }}>🔒 Secured by Razorpay</div>
            </div>
          )}
        </div>

        {/* Right Panel */}
        <div style={rightPanelStyle}>
          {/* Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: isMobile ? "1.5rem" : "2rem",
              paddingBottom: "1rem",
              borderBottom: "1px solid rgba(255, 255, 255, 0.1)",
            }}
          >
            <h2 style={{ color: "white", fontSize: isMobile ? "1.35rem" : "1.75rem", fontWeight: 700, margin: 0 }}>
              Course Registration
            </h2>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? "1.25rem" : "1.5rem" }}>
            {/* MODE TOGGLE */}
            <div>
              <label style={labelText}>Select Mode *</label>

              <div style={modeWrap}>
                <button type="button" style={modeBtn(mode === "online")} onClick={() => setMode("online")}>
                  Online
                </button>
                <button type="button" style={modeBtn(mode === "offline")} onClick={() => setMode("offline")}>
                  Offline
                </button>
                <button type="button" style={modeBtn(mode === "ml-sprint")} onClick={() => setMode("ml-sprint")}>
                  ML Sprint
                </button>
              </div>

              {mode === "offline" && (
                <div style={infoBox}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center", marginBottom: "8px", flexWrap: "wrap" }}>
                    <span
                      style={{
                        padding: "6px 10px",
                        borderRadius: "999px",
                        background: "rgba(102,126,234,0.18)",
                        border: "1px solid rgba(102,126,234,0.26)",
                        color: "#c7d2fe",
                        fontWeight: 800,
                        fontSize: isMobile ? "0.72rem" : "0.75rem",
                        letterSpacing: "0.06em",
                      }}
                    >
                      {offlineInfo.badge}
                    </span>
                    <span style={{ color: "#93c5fd", fontWeight: 800, fontSize: isMobile ? "0.75rem" : "0.8rem" }}>
                      {offlineInfo.note}
                    </span>
                  </div>

                  <div style={smallText}>
                    <div style={{ marginBottom: "8px" }}>
                      <b style={{ color: "#e2e8f0" }}>Locations:</b>
                      <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
                        {offlineInfo.locations.map((l, idx) => (
                          <li key={idx} style={{ marginBottom: "4px" }}>
                            <span style={{ color: "#e2e8f0", fontWeight: 700 }}>{l.city}:</span>{" "}
                            {String(l.place || "").startsWith("http") ? (
                              <a
                                href={l.place}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ color: "#93c5fd", textDecoration: "underline", fontWeight: 800 }}
                              >
                                Open Map
                              </a>
                            ) : (
                              <span style={{ color: "#cbd5e1" }}>{l.place}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div style={{ marginBottom: "6px" }}>
                      <b style={{ color: "#e2e8f0" }}>Start:</b>{" "}
                      <span style={{ color: "#cbd5e1" }}>{offlineInfo.start}</span>
                    </div>

                    <div>
                      <b style={{ color: "#e2e8f0" }}>Support:</b>{" "}
                      <a
                        href={`tel:${offlineInfo.contact.replace(/\s/g, "")}`}
                        style={{ color: "#93c5fd", textDecoration: "underline", fontWeight: 800 }}
                      >
                        {offlineInfo.contact}
                      </a>
                    </div>
                  </div>
                </div>
              )}

              {mode === "ml-sprint" && (
                <div style={infoBox}>
                  <div style={{ display: "flex", justifyContent: "space-between", gap: "10px", alignItems: "center", marginBottom: "8px", flexWrap: "wrap" }}>
                    <span
                      style={{
                        padding: "6px 10px",
                        borderRadius: "999px",
                        background: "rgba(102,126,234,0.18)",
                        border: "1px solid rgba(102,126,234,0.26)",
                        color: "#c7d2fe",
                        fontWeight: 800,
                        fontSize: isMobile ? "0.72rem" : "0.75rem",
                        letterSpacing: "0.06em",
                      }}
                    >
                      {mlSprintInfo.badge}
                    </span>
                    <span style={{ color: "#93c5fd", fontWeight: 800, fontSize: isMobile ? "0.75rem" : "0.8rem" }}>
                      {mlSprintInfo.note}
                    </span>
                  </div>

                  <div style={smallText}>
                    <p style={{ margin: "0 0 10px 0", color: "#e2e8f0" }}>{mlSprintInfo.description}</p>

                    <div style={{ marginBottom: "8px" }}>
                      <b style={{ color: "#e2e8f0" }}>Program Highlights:</b>
                      <ul style={{ margin: "6px 0 0 18px", padding: 0 }}>
                        {mlSprintInfo.features.map((feature, idx) => (
                          <li key={idx} style={{ marginBottom: "4px", color: "#cbd5e1" }}>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <b style={{ color: "#e2e8f0" }}>For Queries:</b>{" "}
                      <a
                        href={`tel:${mlSprintInfo.contact.replace(/\s/g, "")}`}
                        style={{ color: "#93c5fd", textDecoration: "underline", fontWeight: 800 }}
                      >
                        {mlSprintInfo.contact}
                      </a>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Course Selection */}
            <div>
              <label style={labelText}>Select Course *</label>

              <select
                value={selectedCourse}
                onChange={handleCourseSelect}
                style={inputStyle}
                required
                disabled={mode === "offline" || mode === "ml-sprint"}
              >
                <option value="" style={{ background: "#1a1a2e", color: "white" }}>
                  Choose your course...
                </option>

                {filteredCourses.map((course) => (
                  <option key={course.id} value={course.id} style={{ background: "#1a1a2e", color: "white" }}>
                    {course.name} - {course.duration}
                  </option>
                ))}
              </select>
            </div>

            {/* ✅ Payment Method only */}
            <div>
              <label style={labelText}>Payment Method</label>
              <div style={pmGrid}>
                <button type="button" style={pmBtn(payMode === "upi")} onClick={() => setPayMode("upi")}>
                  UPI (No extra charges)
                  <div style={{ marginTop: "6px", fontSize: "0.78rem", color: "#cbd5e1", fontWeight: 700 }}>
                    Recommended
                  </div>
                </button>
                <button type="button" style={pmBtn(payMode === "card_nb")} onClick={() => setPayMode("card_nb")}>
                  Card / NetBanking
                  <div style={{ marginTop: "6px", fontSize: "0.78rem", color: "#cbd5e1", fontWeight: 700 }}>
                    Convenience fee + GST applied
                  </div>
                </button>
              </div>
              <div style={hintStyle}>For Card/NetBanking: 2% convenience fee + 18% GST on the fee.</div>
            </div>

            {/* Personal Info */}
            <div>
              <label style={labelText}>Full Name *</label>
              <input
                type="text"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder="Enter your full name"
                style={inputStyle}
                required
              />
            </div>

            <div>
              <label style={labelText}>Email Address *</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="your.email@example.com"
                style={inputStyle}
                required
              />
            </div>

            <div>
              <label style={labelText}>Phone Number *</label>
              <input
                type="tel"
                name="phoneNumber"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                placeholder="+91-XXXX-XXXXXX"
                style={inputStyle}
                required
              />
            </div>

            {/* Terms */}
            <div
              style={{
                background: "rgba(255, 255, 255, 0.03)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "12px",
                padding: isMobile ? "1rem" : "1.25rem",
              }}
            >
              <label style={{ display: "flex", alignItems: "flex-start", gap: "0.75rem", cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={acceptedTerms}
                  onChange={(e) => setAcceptedTerms(e.target.checked)}
                  style={{
                    marginTop: "3px",
                    minWidth: "16px",
                    height: "16px",
                    accentColor: "#667eea",
                    cursor: "pointer",
                  }}
                  required
                />
                <span style={{ color: "#e2e8f0", fontSize: isMobile ? "0.8rem" : "0.85rem", lineHeight: 1.4 }}>
                  I agree to the{" "}
                  <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" style={{ color: "#667eea", textDecoration: "underline" }}>
                    Terms and Conditions
                  </a>{" "}
                  and{" "}
                  <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" style={{ color: "#667eea", textDecoration: "underline" }}>
                    Privacy Policy
                  </a>
                  .
                </span>
              </label>
            </div>

            {/* Pay Button */}
            <button onClick={handleNext} disabled={loading} style={buttonStyle}>
              {loading ? (
                <>
                  <div className="spinner"></div>
                  Processing Payment...
                </>
              ) : (
                "Proceed to Payment →"
              )}
            </button>

            <div style={{ textAlign: "center", color: "#a0aec0", fontSize: isMobile ? "0.75rem" : "0.8rem" }}>
              🔒 Your information is secure and encrypted
            </div>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {showSuccessModal && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.95)",
            backdropFilter: "blur(10px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
            animation: "fadeIn 0.3s ease",
          }}
        >
          <div
            style={{
              background: "linear-gradient(135deg, rgba(30, 41, 59, 0.98), rgba(15, 23, 42, 0.95))",
              border: "2px solid rgba(102, 126, 234, 0.3)",
              borderRadius: "24px",
              padding: isMobile ? "2rem 1.5rem" : "3rem",
              maxWidth: isMobile ? "90%" : "520px",
              width: "100%",
              textAlign: "center",
              position: "relative",
              overflow: "hidden",
              boxShadow: "0 20px 60px rgba(0, 0, 0, 0.5)",
              color: "white",
            }}
          >
            <h2 style={{ marginTop: 0, marginBottom: "0.75rem" }}>Payment Successful!</h2>

            <div style={{ color: "#cbd5e1", lineHeight: 1.6 }}>
              Mode: <b style={{ color: "white" }}>{mode.toUpperCase().replace("-", " ")}</b>
              <br />
              Course: <b style={{ color: "white" }}>{selectedCourseData?.name}</b>
              <br />
              Payment Mode: <b style={{ color: "white" }}>{payMode === "upi" ? "UPI" : "Card / NetBanking"}</b>
              <br />
              Estimated Total: <b style={{ color: "white" }}>₹{fmtINR(totalPayable)}</b>
              <br />
              Name: <b style={{ color: "white" }}>{formData.fullName}</b>
            </div>

            <button
              onClick={handleSuccessModalClose}
              style={{
                marginTop: "1.25rem",
                padding: isMobile ? "1rem" : "0.9rem",
                width: "100%",
                borderRadius: "12px",
                border: "none",
                background: "linear-gradient(135deg, #667eea, #764ba2)",
                color: "white",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              Continue
            </button>

            <button
              onClick={() => (window.location.href = "/")}
              style={{
                marginTop: "0.75rem",
                padding: isMobile ? "1rem" : "0.9rem",
                width: "100%",
                borderRadius: "12px",
                border: "1px solid rgba(255,255,255,0.2)",
                background: "transparent",
                color: "#a0aec0",
                fontWeight: 700,
                cursor: "pointer",
              }}
            >
              Back to Home
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default CourseRegistrationPage;
